import useOverlay from "@/hooks/common/useOverlay";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import {getPrice, isGoodsTpFH} from "@COMPONENTS/common/eventUtil";
import {getExpectMileage} from "@COMPONENTS/common/goodsUtil";
import PointSavePop from "@COMPONENTS/product/common/popup/mobile/PointSavePop";
import {TDetailPackage} from "@COMPONENTS/product/types";
import * as style from "@COMPONENTS/style/detailpackage/itineraryM";
import {Area} from "@FRAMEWORK/ui/mobile/layout";
import BulletListItem from "@FRAMEWORK/ui/mobile/list/BulletListItem";
import {SubTitle} from "@FRAMEWORK/ui/mobile/text";
import useEventMileage from "@QUERIES/prdt/event/useEventMileage";
import useEventPrice from "@QUERIES/prdt/event/useEventPrice";
import {format} from "@yb/utils";
import {memo} from "react";

interface Props extends TDetailPackage {
	/**
	 * @description 포인트 적립 팝업 버튼 노출여부
	 */
	usePointBtn?: boolean;
}

const DetailPriceComponent = (props: Props) => {
	const {eventDetail: event, query, usePointBtn = true} = props;
	const [open] = useOverlay();
	const eventPriceFetch = useEventPrice(query.evCd);
	const eventPrice = eventPriceFetch.data?.body;
	const useEvtMileage = useEventMileage(query.evCd);
	const mileage = useEvtMileage.data?.body || [];
	const price = getPrice(eventPrice);

	const TdPrice = ({type = "adult"}) => {
		const amount = {
			adult: {price: price.adult, baf: eventPrice.bafAdtCost, air: eventPrice.airTaxAdtCost},
			child: {price: price.child, baf: eventPrice.bafChdCost, air: eventPrice.airTaxChdCost},
			infant: {price: price.infant, baf: eventPrice.bafInfCost, air: eventPrice.airTaxInfCost},
		};
		const cost = amount[type];
		const isFH = isGoodsTpFH(event.goodsTp);

		return (
			<>
				{!isFH ? (
					<td>
						<style.TablePriceBox>
							<em>
								<strong>{price && format.koKRFormatNumber(cost.price)}</strong>원
							</em>
							{(cost.baf > 0 || cost.air > 0) && (
								<p>{`세금 및 유류할증료 ${format.koKRFormatNumber(cost.baf + cost.air)}원 포함`}</p>
							)}
						</style.TablePriceBox>
					</td>
				) : (
					<td>
						{cost.price > 0 ? (
							<style.TablePriceBox>
								<em>
									<strong>{price && format.koKRFormatNumber(cost.price)}</strong>원
								</em>
								{(cost.baf > 0 || cost.air > 0) && (
									<p>{`세금 및 유류할증료 ${format.koKRFormatNumber(cost.baf + cost.air)}원 포함`}</p>
								)}
							</style.TablePriceBox>
						) : (
							<style.TablePriceBox>별도문의</style.TablePriceBox>
						)}
					</td>
				)}
			</>
		);
	};

	return (
		<Area>
			<style.TitleWrap>
				<SubTitle>상품가</SubTitle>
				<style.SubText>
					{/* 실예약(전액) 인 경우 비노출 */}
					{event.fixRervCd !== "YT" && (
						<>
							(예약금){" "}
							{eventPrice.depositAmt === 0 ? (
								<>별도안내</>
							) : (
								<>
									{`${eventPrice.depositDivi ? eventPrice.depositDiviNm : ""} ${format.koKRFormatNumber(
										eventPrice.depositAmt,
									)}원`}
								</>
							)}
						</>
					)}
				</style.SubText>
			</style.TitleWrap>
			<style.PriceTable types="vertical">
				<caption>여행지 안전 정보 안내</caption>
				<colgroup>
					<col style={{width: "33.333%"}} />
					<col style={{width: "33.333%"}} />
					<col style={{width: "33.333%"}} />
				</colgroup>
				<thead>
					<tr>
						<th scope="col">
							<style.AgeLimitBox>
								성인
								<style.AgeLimit>(만12세 이상)</style.AgeLimit>
							</style.AgeLimitBox>
						</th>
						<th scope="col">
							<style.AgeLimitBox>
								아동
								<style.AgeLimit>(만2세~만12세 미만)</style.AgeLimit>
							</style.AgeLimitBox>
						</th>
						<th scope="col">
							<style.AgeLimitBox>
								유아
								<style.AgeLimit>(만2세 미만)</style.AgeLimit>
							</style.AgeLimitBox>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						{price && <TdPrice />}
						{price && <TdPrice type="child" />}
						{price && <TdPrice type="infant" />}
					</tr>
				</tbody>
			</style.PriceTable>
			<style.Point>
				{usePointBtn && (
					<style.TitleTextBox
						href="#"
						target="_blank"
						onClick={e => {
							e.preventDefault();
							open(overlay => <PointSavePop {...overlay} evCd={query.evCd} />);
						}}
					>
						(성인1인기준) {mileage && format.koKRFormatNumber(getExpectMileage(mileage))} P 적립예정
						<style.PopupButton>
							<em>포인트 적립 팝업으로 이동</em>
						</style.PopupButton>
					</style.TitleTextBox>
				)}
			</style.Point>
			<style.BulletBox>
				<ul>
					<BulletListItem>
						유류할증료 및 TAX는 유가 및 환율 등에 따라 변동될 수 있으며, 아동/유아 기준은 항공사 마다 상이하여 예약 후
						최종 확정됩니다.
					</BulletListItem>
				</ul>
			</style.BulletBox>
		</Area>
	);
};

const DetailPrice = (props: Props) => (
	<ErrorBoundary logMsg="EventDetailPrice Error">
		<DetailPriceComponent {...props} />
	</ErrorBoundary>
);

export default memo(DetailPrice);
